import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { Icon, IconID } from '@hub/common/Icon/Icon';
import { APP_NAME, AVAILABLE_PAYMENT_METHODS, getLocale } from '@hub/libs/constants';
import { createMarkup } from '@hub/libs/utils';
import styles from './shopinfo.module.css';
import classNames from 'classnames';
import { Link } from '@hub/common/Link/Link';
import { useBreakpoints } from '@hub/hooks/useBreakpoints';
import { useRouter } from 'next/router';
import { EXTERNAL_LINKS, IGUS_REWRITE } from '@hub/libs/redirect.helper';

interface LinkItem {
  icon: IconID;
  text: string;
  link: string;
}

export const ShopInfo = () => {
  const { t } = useTranslation(['layout/shopInfo', 'layout/footer']);
  const { md } = useBreakpoints();
  const router = useRouter();
  const locale = getLocale(router);
  const items = {
    main: [
      {
        icon: IconID.ASSORTMENT,
        text: t('SHOP_INFO.PRODUCT_COUNT'),
        link: EXTERNAL_LINKS.SHOP_HOMEPAGE(locale),
      },
      {
        icon: IconID.H24_DELIVERY,
        text: t('SHOP_INFO.24H_DELIVERY'),
        link: IGUS_REWRITE(t('SHOP_INFO.DELIVERY_SERVICE_URL'), locale),
      },
      {
        icon: IconID.DELIVERY_1_PIECE,
        text: t('SHOP_INFO.NO_MINIMUM'),
        link: IGUS_REWRITE(t('SHOP_INFO.DELIVERY_SERVICE_URL'), locale),
      },
      { icon: IconID.FILE_CAD, text: t('SHOP_INFO.CAD_DOWNLOAD'), link: t('SHOP_INFO.CAD_DOWNLOAD_URL') },
      {
        icon: IconID.FREE_SAMPLE,
        text: t('SHOP_INFO.FREE_SAMPLE'),
        link: IGUS_REWRITE(t('SHOP_INFO.FREE_SAMPLE_URL'), locale),
      },
      {
        icon: IconID.CONSULTING,
        text: t('SHOP_INFO.CONTACT'),
        link: IGUS_REWRITE(t('SHOP_INFO.CONTACT_URL'), locale),
      },
    ],
    quality: [
      {
        icon: IconID.ARROW_RIGHT,
        text: t('SHOP_INFO.PRODUCER_FOR_ALL'),
        link: IGUS_REWRITE(t('FOOTER.LINKS.COMPANY_URL', { ns: 'layout/footer' }), locale),
      },
      {
        icon: IconID.ARROW_RIGHT,
        text: t('SHOP_INFO.QUALITY_PROMISE'),
        link: IGUS_REWRITE(t('SHOP_INFO.QUALITY_URL'), locale),
      },
    ],
    support: [
      { icon: IconID.PHONE, text: t('SHOP_INFO.CONTACT_PHONE'), link: t('SHOP_INFO.CONTACT_PHONE_URL') },
      {
        icon: IconID.MAIL,
        text: t('SHOP_INFO.CONTACT_FORM'),
        link: IGUS_REWRITE(t('SHOP_INFO.CONTACT_FORM_URL'), locale),
      },
      {
        icon: IconID.ARROW_RIGHT,
        text: t('SHOP_INFO.MORE_CONTACT'),
        link: IGUS_REWRITE(t('SHOP_INFO.CONTACT_URL'), locale),
      },
    ],
    feedback: [
      {
        icon: IconID.ARROW_RIGHT,
        text: t('SHOP_INFO.CUSTOMER_SURVEY'),
        link: IGUS_REWRITE(t('SHOP_INFO.CUSTOMER_SURVEY_URL'), locale),
      },
      {
        icon: IconID.ARROW_RIGHT,
        text: t('SHOP_INFO.WEBSITE_SURVEY'),
        link: IGUS_REWRITE(t('SHOP_INFO.WEBSITE_SURVEY_URL'), locale),
      },
    ],
  };

  const renderLinkList = (links: LinkItem[]) => (
    <ul className="w-4/5 mt-4">
      {links.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={index} className="py-1.5">
          <Link
            className="text-2xl text-gray-500"
            icon={item.icon}
            bigIcon={item.icon != IconID.ARROW_RIGHT}
            iconPrimary={false}
            text={item.text}
            href={item.link}
          />
        </li>
      ))}
    </ul>
  );

  return (
    <div className="container igus-text-styles">
      <h2 className="bg-igus-primary text-white font-medium px-8 py-4">{t('SHOP_INFO.HEADLINE.MAIN')}</h2>
      <div className="bg-gray-100 text-gray-500 flex flex-col md:flex-row md:justify-around py-8 px-8 md:px-0">
        {items.main.map((newItem, index) => {
          const currentItem = newItem;
          // TODO: find a better solution, this is just a workaround to reuse the translation labels with hard line breaks
          if (!md) {
            currentItem.text = newItem.text.replace('<br/>', ' ').replace('- ', '');
          }
          return (
            <a
              /* eslint-disable-next-line react/no-array-index-key */
              key={index}
              href={currentItem.link}
              className={classNames(
                'hover:text-igus-primary',
                'md:text-center',
                'text-black',
                'leading-3xl',
                'p-2',
                'md:p-6',
                'text-2xl',
                styles.noShadowLink
              )}
            >
              <Icon id={currentItem.icon} size={10} primary={false} />
              <span className="ml-4 md:ml-0 md:block mt-2" dangerouslySetInnerHTML={createMarkup(currentItem.text)} />
            </a>
          );
        })}
      </div>
      <div className="grid grid-cols-1 gap-y-12 sm:gap-y-0 sm:grid-cols-2 md:grid-cols-3 mt-12 font-medium text-gray-500 shop-info-grid">
        <div>
          <h3>{t('SHOP_INFO.HEADLINE.PAYMENT')}</h3>
          <ul>
            {AVAILABLE_PAYMENT_METHODS(locale).map(item => (
              <li key={item} className="bg-gray-100 p-4 my-4 text-center text-xl w-80 lg:w-96">
                {t(`SHOP_INFO.PAYMENT.${item}`)}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3>{t('SHOP_INFO.HEADLINE.QUALITY')}</h3>
          {renderLinkList(items.quality)}
        </div>
        <div>
          <h3>{t('SHOP_INFO.HEADLINE.SUPPORT')}</h3>
          {renderLinkList(items.support)}
        </div>
        {locale === 'de-DE' && (
          <div className="md:col-span-2 sm:mt-12">
            <h3>{t('SHOP_INFO.HEADLINE.VERIFIED')}</h3>
            <a
              className="mt-2 inline-block"
              href={t('SHOP_INFO.TRUSTED_SHOPS_URL')}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Trusted Shop"
            >
              <Image
                width={200}
                height={100}
                src={`/_${APP_NAME}/images/trusted-shops-badge.png`}
                alt="Trusted Shops Badge"
              />
            </a>
          </div>
        )}
        <div className="sm:mt-12">
          <h3>{t('SHOP_INFO.HEADLINE.FEEDBACK')}</h3>
          {renderLinkList(items.feedback)}
        </div>
      </div>
    </div>
  );
};
